import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, MessageCircle, Lock, Globe, Heart, Search, MoreVertical, Edit3, Trash2, LogOut, FileEdit, Flag, Sparkles, HeartHandshake, Sun, Activity, Brain, Flower2 } from 'lucide-react';
import { toast } from 'sonner';
import ReportModal from '../components/ReportModal';

const SupportGroups = ({ user }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [joining, setJoining] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    category: 'general',
    is_private: false
  });
  const [openMenuGroupId, setOpenMenuGroupId] = useState(null);
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [leavingGroup, setLeavingGroup] = useState(null);
  const [reportingUser, setReportingUser] = useState(null);
  const [changingPrivacy, setChangingPrivacy] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    category: '',
    is_private: false
  });

  const categories = [
    { id: 'all', name: 'All Groups', icon: Sparkles, color: 'from-amber-400 to-yellow-300' },
    { id: 'anxiety', name: 'Anxiety Support', icon: HeartHandshake, color: 'from-blue-400 to-cyan-300' },
    { id: 'depression', name: 'Depression', icon: Sun, color: 'from-orange-400 to-amber-300' },
    { id: 'relationships', name: 'Relationships', icon: Heart, color: 'from-pink-400 to-rose-300' },
    { id: 'general', name: 'General Wellness', icon: Activity, color: 'from-emerald-400 to-green-300' },
    { id: 'mindfulness', name: 'Mindfulness', icon: Brain, color: 'from-purple-400 to-violet-300' },
    { id: 'therapy', name: 'Therapy Journey', icon: MessageCircle, color: 'from-indigo-400 to-blue-300' }
  ];

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load support groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/groups/create', newGroup);
      toast.success('Support group created successfully! 🎉');
      setShowCreateForm(false);
      setNewGroup({ name: '', description: '', category: 'general', is_private: false });
      fetchGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const handleJoinGroup = async (groupId) => {
    setJoining(groupId);
    try {
      await axios.post(`/groups/${groupId}/join`);
      toast.success('Successfully joined the group! 🤗');
      fetchGroups();
    } catch (error) {
      console.error('Error joining group:', error);
      toast.error('Failed to join group');
    } finally {
      setJoining(null);
    }
  };

  const isUserInGroup = (group) => {
    return group.members && group.members.includes(user.id);
  };

  const isGroupCreator = (group) => {
    return group.creator_id === user.id;
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category || { icon: Sparkles, color: 'from-amber-400 to-yellow-300' };
  };

  const handleRenameGroup = (group) => {
    setRenamingGroup(group);
    setOpenMenuGroupId(null);
  };

  const handleSaveRename = async () => {
    if (!renamingGroup.name.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      await axios.put(`/groups/${renamingGroup.id}`, {
        name: renamingGroup.name.trim(),
        description: renamingGroup.description,
        category: renamingGroup.category,
        is_private: renamingGroup.is_private
      });
      toast.success('Group renamed successfully!');
      setRenamingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error renaming group:', error);
      toast.error('Failed to rename group');
    }
  };

  const handleEditGroup = (group) => {
    setEditFormData({
      name: group.name,
      description: group.description,
      category: group.category,
      is_private: group.is_private
    });
    setEditingGroup(group);
    setOpenMenuGroupId(null);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.name.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      await axios.put(`/groups/${editingGroup.id}`, editFormData);
      toast.success('Group updated successfully!');
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await axios.delete(`/groups/${deletingGroup.id}`);
      toast.success('Group deleted successfully');
      setDeletingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await axios.post(`/groups/${leavingGroup.id}/leave`);
      toast.success('Left group successfully');
      setLeavingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error('Failed to leave group');
    }
  };

  const handlePrivacyChange = async () => {
    try {
      const newPrivacyStatus = !changingPrivacy.is_private;
      
      await axios.put(`/groups/${changingPrivacy.id}`, {
        name: changingPrivacy.name,
        description: changingPrivacy.description,
        category: changingPrivacy.category,
        is_private: newPrivacyStatus
      });

      const statusText = newPrivacyStatus ? 'private' : 'public';
      toast.success(`This group is now ${statusText}`, {
        description: `All members have been notified about the privacy change.`,
        duration: 5000
      });
      
      setChangingPrivacy(null);
      fetchGroups();
    } catch (error) {
      console.error('Error changing group privacy:', error);
      toast.error('Failed to change group privacy');
    }
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || group.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="groups-loading">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading support groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="support-groups">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Support Groups</h1>
          <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
            Join safe, supportive communities where you can share experiences and grow together.
          </p>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full"
                data-testid="search-groups"
              />
            </div>
          </div>

          {/* Create Group Button */}
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary flex items-center space-x-2 whitespace-nowrap"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4" />
            <span>Create Group</span>
          </button>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r ' + category.color + ' text-white shadow-lg'
                      : 'bg-white text-emerald-700 hover:bg-emerald-50 border-2 border-emerald-100'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group, index) => (
            <div key={group.id} className={`card animate-fade-in-up stagger-${(index % 3) + 1}`} data-testid={`group-card-${group.id}`}>
              {/* Group Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  {(() => {
                    const categoryData = getCategoryIcon(group.category);
                    const IconComponent = categoryData.icon;
                    return (
                      <div className={`w-12 h-12 bg-gradient-to-br ${categoryData.color} rounded-xl flex items-center justify-center shadow-md`}>
                        <IconComponent className="w-6 h-6 text-white" strokeWidth={2.5} />
                      </div>
                    );
                  })()}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-emerald-800 line-clamp-1">{group.name}</h3>
                    <div className="flex items-center space-x-2 text-sm text-emerald-600">
                      {group.is_private ? (
                        <>
                          <Lock className="w-3 h-3" />
                          <span>Private</span>
                        </>
                      ) : (
                        <>
                          <Globe className="w-3 h-3" />
                          <span>Public</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="group-badge" style={{background: 'linear-gradient(135deg, #8b5cf6, #6366f1)'}}>
                    {group.category}
                  </span>
                  
                  {/* Three-Dot Menu */}
                  {isUserInGroup(group) && (
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuGroupId(openMenuGroupId === group.id ? null : group.id);
                        }}
                        className="p-1 hover:bg-emerald-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-5 h-5 text-emerald-600" />
                      </button>
                      
                      {openMenuGroupId === group.id && (
                        <>
                          <div 
                            className="fixed inset-0 z-10" 
                            onClick={() => setOpenMenuGroupId(null)}
                          />
                          
                          <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-700 rounded-xl shadow-2xl z-20 overflow-hidden">
                            {/* Rename Group (Creator Only) */}
                            {isGroupCreator(group) && (
                              <button
                                onClick={() => handleRenameGroup(group)}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors"
                              >
                                <span className="text-emerald-800 dark:text-emerald-100 font-medium">Rename Group</span>
                              </button>
                            )}
                            
                            {/* Edit Group (Creator Only) */}
                            {isGroupCreator(group) && (
                              <button
                                onClick={() => handleEditGroup(group)}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors border-t border-emerald-100 dark:border-slate-700"
                              >
                                <span className="text-emerald-800 dark:text-emerald-100 font-medium">Edit Details</span>
                              </button>
                            )}
                            
                            {/* Change Privacy (Creator Only) */}
                            {isGroupCreator(group) && (
                              <button
                                onClick={() => {
                                  setChangingPrivacy(group);
                                  setOpenMenuGroupId(null);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors border-t border-emerald-100 dark:border-slate-700"
                              >
                                <span className="text-emerald-800 dark:text-emerald-100 font-medium">
                                  {group.is_private ? 'Make Public' : 'Make Private'}
                                </span>
                              </button>
                            )}
                            
                            {/* Delete Group (Creator Only) */}
                            {isGroupCreator(group) && (
                              <button
                                onClick={() => {
                                  setDeletingGroup(group);
                                  setOpenMenuGroupId(null);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors border-t border-emerald-100 dark:border-slate-700"
                              >
                                <span className="text-emerald-800 dark:text-emerald-100 font-medium">Delete Group</span>
                              </button>
                            )}
                            
                            {/* Leave Group (Non-Creator Members) */}
                            {!isGroupCreator(group) && (
                              <button
                                onClick={() => {
                                  setLeavingGroup(group);
                                  setOpenMenuGroupId(null);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors"
                              >
                                <span className="text-emerald-800 dark:text-emerald-100 font-medium">Leave Group</span>
                              </button>
                            )}
                            
                            {/* Report Group Creator/Member */}
                            <button
                              onClick={() => {
                                setReportingUser({
                                  id: group.creator_id,
                                  name: isGroupCreator(group) ? 'this group' : 'group creator',
                                  contextId: group.id
                                });
                                setOpenMenuGroupId(null);
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ${isGroupCreator(group) || !isGroupCreator(group) ? 'border-t border-red-100 dark:border-red-900/30' : ''}`}
                            >
                              <span className="text-red-600 font-medium">Report {isGroupCreator(group) ? 'Issue' : 'Group'}</span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="text-emerald-700 text-sm mb-4 line-clamp-3">{group.description}</p>

              {/* Members Count */}
              <div className="flex items-center space-x-2 mb-4 text-sm text-emerald-600">
                <Users className="w-4 h-4" />
                <span>{group.members ? group.members.length : 0} members</span>
              </div>

              {/* Action Button */}
              <div className="flex space-x-2">
                {isUserInGroup(group) ? (
                  <button className="btn-secondary flex-1 flex items-center justify-center space-x-2" disabled>
                    <Heart className="w-4 h-4 text-emerald-600" />
                    <span>Joined</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleJoinGroup(group.id)}
                    disabled={joining === group.id}
                    className="btn-primary flex-1 flex items-center justify-center space-x-2 disabled:opacity-50"
                    data-testid={`join-group-${group.id}`}
                  >
                    {joining === group.id ? (
                      <div className="spinner border-white"></div>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Join Group</span>
                      </>
                    )}
                  </button>
                )}
                
                {isUserInGroup(group) && (
                  <button 
                    onClick={() => window.location.href = `/groups/${group.id}/chat`}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>Chat</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* No Groups Found */}
        {filteredGroups.length === 0 && (
          <div className="text-center py-16">
            <div className="card max-w-md mx-auto">
              <Users className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">No Groups Found</h3>
              <p className="text-emerald-600 mb-6">
                {searchTerm || selectedCategory !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Be the first to create a support group for your community!'}
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setShowCreateForm(true);
                }}
                className="btn-primary"
              >
                Create First Group
              </button>
            </div>
          </div>
        )}

        {/* Create Group Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-fade-in-scale" data-testid="create-group-modal">
              <h2 className="text-xl font-semibold text-emerald-800 mb-6">Create Support Group</h2>
              
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                    className="form-input w-full"
                    placeholder="Enter group name"
                    data-testid="group-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Description
                  </label>
                  <textarea
                    required
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                    className="form-input w-full min-h-[100px] resize-none"
                    placeholder="Describe the purpose and goals of this group"
                    data-testid="group-description-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Category
                  </label>
                  <select
                    value={newGroup.category}
                    onChange={(e) => setNewGroup({...newGroup, category: e.target.value})}
                    className="form-input w-full"
                    data-testid="group-category-select"
                  >
                    {categories.filter(cat => cat.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="private"
                    checked={newGroup.is_private}
                    onChange={(e) => setNewGroup({...newGroup, is_private: e.target.checked})}
                    className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="group-private-checkbox"
                  />
                  <label htmlFor="private" className="text-sm text-emerald-700">
                    Make this group private
                  </label>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1 flex items-center justify-center space-x-2"
                    data-testid="create-group-submit"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Rename Group Modal */}
        {renamingGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-4">Rename Group</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={renamingGroup.name}
                    onChange={(e) => setRenamingGroup({...renamingGroup, name: e.target.value})}
                    className="form-input w-full"
                    placeholder="Enter new group name"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setRenamingGroup(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRename}
                  className="flex-1 btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Group Modal */}
        {editingGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-4">Edit Group Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    className="form-input w-full"
                    placeholder="Enter group name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    className="form-input w-full min-h-[100px] resize-none"
                    placeholder="Describe the purpose of this group"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Category
                  </label>
                  <select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                    className="form-input w-full"
                  >
                    {categories.filter(cat => cat.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-private"
                    checked={editFormData.is_private}
                    onChange={(e) => setEditFormData({...editFormData, is_private: e.target.checked})}
                    className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="edit-private" className="text-sm text-emerald-700">
                    Make this group private
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setEditingGroup(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Group Confirmation Modal */}
        {deletingGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-3">Delete Group</h3>
              <p className="text-emerald-600 mb-6">
                Are you sure you want to delete "{deletingGroup.name}"? All messages and data will be permanently deleted. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeletingGroup(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteGroup}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leave Group Confirmation Modal */}
        {leavingGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-3">Leave Group</h3>
              <p className="text-emerald-600 mb-6">
                Are you sure you want to leave "{leavingGroup.name}"? You can rejoin this group later.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setLeavingGroup(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLeaveGroup}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Privacy Confirmation Modal */}
        {changingPrivacy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  changingPrivacy.is_private ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {changingPrivacy.is_private ? (
                    <Globe className="w-6 h-6 text-green-600" />
                  ) : (
                    <Lock className="w-6 h-6 text-amber-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-emerald-800">
                    {changingPrivacy.is_private ? 'Make Group Public?' : 'Make Group Private?'}
                  </h3>
                  <p className="text-sm text-emerald-600">"{changingPrivacy.name}"</p>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-3">
                  {changingPrivacy.is_private ? (
                    <>
                      <strong>Making this group public means:</strong>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Anyone can discover and join this group</li>
                        <li>Group will appear in public search results</li>
                        <li>No invitation required to join</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <strong>Making this group private means:</strong>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Only invited members can join</li>
                        <li>Group won't appear in public searches</li>
                        <li>More control over group membership</li>
                      </ul>
                    </>
                  )}
                </p>
                <p className="text-sm text-blue-700 font-medium mt-3">
                  📢 All {changingPrivacy.members?.length || 0} members will be notified about this change
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setChangingPrivacy(null)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrivacyChange}
                  className={`flex-1 px-4 py-3 text-white rounded-xl transition-colors font-medium shadow-lg hover:shadow-xl ${
                    changingPrivacy.is_private
                      ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                      : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                  }`}
                >
                  {changingPrivacy.is_private ? 'Make Public' : 'Make Private'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report User Modal */}
        {reportingUser && (
          <ReportModal
            isOpen={!!reportingUser}
            onClose={() => setReportingUser(null)}
            reportedUserId={reportingUser.id}
            reportedUserName={reportingUser.name}
            reportType="group"
            contextId={reportingUser.contextId}
          />
        )}
      </div>
    </div>
  );
};

export default SupportGroups;